import React, { useEffect, useState } from 'react';
import cn from "classnames";
import useTrans from "components/hooks/useTrans";
import Text from "components/typography/Text";
import useFetcher from "../../../http/useFetcher";
import SelectPlan from "components/pricing/calculator/SelectPlan_v2";
import SelectTier_v2 from './SelectTier_v2';
import Price_v2 from "components/pricing/calculator/Price_v2";
import UpgradeBar_v2 from './UpgradeBar_v2';
import { PricingDataMapper } from "blocks/PricingDataMapper";
import Input from "components/elements/Input";
import CurrencySwitcher from "components/base/CurrencySwitcher";

export class PricingData {
    plans: Plan[];
}

export interface Plan {
    id: string;
    type: string;
    itemPrices: ItemPrice[];
}

export interface ItemPrice {
    id: string;
    periodUnit: string;
    tiers: Tier[];
}

export interface Tier {
    endingUnit: number;
    displayQuantity: [];
    displayPrice: [];
    displaySimplePrice: [];
    displayFullPrice: [];
    displayPricePerMonth: [];
}

type Props = {
    currency: string;
    setCurrency: any;
};

export default function Calculator(props: Props) {
    const { currency, setCurrency } = props;
    const { t } = useTrans();
    const [data, setData] = useState(null);
    const [plans, setPlans] = useState<Plan[]>(null);
    const [selectedPlan, setSelectedPlan] = useState<Plan>(null);
    const [tiers, setTiers] = useState<Tier[]>(null);
    const [sliderIndex, setSliderIndex] = useState(0);
    const [sliderHasMoved, setSliderHasMoved] = useState(false);
    const [period, setPeriod] = useState<"year" | "month">("year");
    const [inputNumber, setInputNumber] = useState("1000");

    const isLastOfTier = tiers ? sliderIndex === tiers.length - 1 : false;

    const findTiersForPlan = (plan: Plan) => {
        return plan.itemPrices.find((p: any) => p.periodUnit === period).tiers;
    };

    useFetcher({
        url: '/pricing',
        mapper: PricingDataMapper,
        dataSetter: setData,
    });

    useEffect(() => {
        if (data) {
            setPlans(data.plans);
            setSelectedPlan(data.plans[0]);
        }
    }, [data]);

    useEffect(() => {
        if (selectedPlan) {
            setTiers(findTiersForPlan(selectedPlan));
        }
    }, [selectedPlan, period]);

    useEffect(() => {
        if (selectedPlan && !sliderHasMoved) {
            if (selectedPlan.id == "Enterprise") {
                const index = findTiersForPlan(selectedPlan).findIndex(t => t.endingUnit === 1000);
                setSliderIndex(index);
            }

            if (selectedPlan.id == "Starter") {
                const index = findTiersForPlan(selectedPlan).findIndex(t => t.endingUnit === 1000);
                setSliderIndex(index);
            }

            if (selectedPlan.id == "Professional") {
                const index = findTiersForPlan(selectedPlan).findIndex(t => t.endingUnit === 1000);
                setSliderIndex(index);
            }
        }

        if (sliderHasMoved) {
            const currentTier = tiers[sliderIndex];
            const newTiers = findTiersForPlan(selectedPlan);
            let index = newTiers.findIndex(t => t.endingUnit >= currentTier.endingUnit);
            if (index < 0) {
                index = newTiers.length - 1;
            }
            setSliderIndex(index);
        }
    }, [selectedPlan]);

    useEffect(() => {
        if (tiers) {
            setInputNumber(tiers[sliderIndex].endingUnit.toString());
        }
    }, [sliderIndex]);

    const findClosestIndex = (number: number): number => {
        let index = tiers.findIndex(t => t.endingUnit >= number);
        if (index < 0) {
            index = 0;
        }

        return index;
    };

    const calculatorContainerCn = cn(
        'bg-white relative z-10 rounded-xl py-10 px-6 lg:p-12 lg:pt-8'
    );

    return (
        <div className={"pb-10"}>
            <div className={'mb-12 text-center'}>
                <Text
                    weight={'medium'}
                    size={'4xl'}
                    color={'text-gray-900'}
                >
                    {t('calculateYourPrice')}
                </Text>
            </div>
            <div className={"w-full flex justify-end p-3"}>
                <CurrencySwitcher currency={currency} setCurrency={setCurrency}/>
            </div>
            <div className={calculatorContainerCn}>
                <div className={'flex justify-center mb-5'}>
                    <Text weight={"medium"} color={"text-gray-400"}>{t('selectPlan')}</Text>
                </div>
                <div className={" lg:flex "}>
                    <div className={"lg:flex lg:flex-col lg:w-8/12 lg:pr-10 lg:border-r lg:border-primary-25"}>
                        <SelectPlan
                            plans={plans}
                            selectedPlan={selectedPlan}
                            setSelectedPlan={setSelectedPlan}
                        />
                        <div className={"mt-8 mb-4 lg:mb-10 flex justify-between items-end gap-2"}>
                            <div>
                                {tiers && (
                                    <div className={'mb-1'}>
                                <span className={"text-gray-900 font-normal lg:text-lg"}>
                                    {t('pricingStartsAt', { quantity: tiers[0].endingUnit })}
                                </span>
                                    </div>
                                )}
                                <span
                                    className={"text-gray-500 lg:text-primary-400 font-light lg:font-normal text-base lg:text-lg"}
                                >
                                {t('selectYourAudience', { periodUnit: t(period) })}
                            </span>
                            </div>
                            <form
                                className={"flex justify-end"}
                                onSubmit={(event) => {
                                    const index = findClosestIndex(parseInt(inputNumber));
                                    setSliderIndex(index);
                                    event.preventDefault();
                                }}
                            >
                                <Input
                                    type={"number"}
                                    placeholder={""}
                                    value={inputNumber}
                                    onChange={(event: any) => setInputNumber(event.target.value)}
                                    onBlur={(event: any) => {
                                        const index = findClosestIndex(event.target.value);
                                        setSliderIndex(index);
                                        setInputNumber(tiers[index].endingUnit.toString());
                                    }}
                                    className={"max-w-[125px] hidden lg:inline"}
                                />
                            </form>
                        </div>

                        <SelectTier_v2
                            selectedPlan={selectedPlan}
                            tiers={tiers}
                            sliderIndex={sliderIndex}
                            setSliderIndex={setSliderIndex}
                            sliderHasMoved={sliderHasMoved}
                            setSliderHasMoved={setSliderHasMoved}
                            currency={currency}
                        />
                    </div>

                    {tiers && (
                        <Price_v2
                            selectedTier={tiers[sliderIndex]}
                            period={period}
                            setPeriod={setPeriod}
                            currency={currency}
                        />
                    )}
                </div>
            </div>

            {tiers && tiers[sliderIndex] && (
                <UpgradeBar_v2
                    active={isLastOfTier}
                    quantity={tiers[sliderIndex].endingUnit}
                />
            )}
        </div>
    );
}